/**
 * @file Animation procedurale data-driven du chat (CAT_ANIM) : idle (respiration, queue, oreilles qui tressautent), marche (pattes, rebond), tir (recul + flash), visee du buste.
 */
import { Euler, Quaternion, Vector3 } from 'three';
import { CAT_ANIM } from '@config/index';
import { CAT_LAYOUT } from './CatRig';
import type { CatAnimInput, CatAnimState, CatRig } from './CatRig';

/**
 * Convention d'angle : core 0 rad = +x, sens trigo ; core (x, y) -> Three (x, 0, -y). Un Object3D regarde +Z a rotation.y = 0,
 * donc la direction core `a` correspond a rotation.y = a + PI/2 (a=0 -> +X Three ; a=PI/2 -> -Z Three).
 */
export const coreAngleToYaw = (a: number): number => a + Math.PI / 2;

const DEG = Math.PI / 180;
const TAU = Math.PI * 2;
/** Ecart maximal buste/arme par rapport au cap : au-dela, le corps pivote (il ne se tord jamais de plus de 100 deg). */
const UPPER_YAW_LIMIT = 100 * DEG;
/** Part du lacet du buste ajoutee a la tete. */
const HEAD_YAW_SHARE = 0.4;
/** Fraction de l'amplitude de jambe appliquee aux pattes avant (elles tiennent l'arme). */
const ARM_SWING_SHARE = 0.2;
/** Dephasage des segments de queue (onde propagee) et amplitude relative par segment. */
const TAIL_PHASE_STEP = 0.6;
const TAIL_AMP_BASE = 0.5;
const TAIL_AMP_STEP = 0.25;
/** Vitesse normalisee minimale en marche (evite une cadence nulle a faible vitesse). */
const MIN_STEP_SPEED = 0.4;
/** Vitesse de fondu idle <-> marche (1/s) et decroissance du tressautement d'oreille (par 1/60 s). */
const WALK_BLEND_RATE = 10;
const EAR_DECAY_PER_FRAME = 0.85;
const FRAME_60 = 1 / 60;
/** Les bras suivent legerement le recul de l'arme (rad par unite de recul). */
const ARM_RECOIL_PER_UNIT = 1.2;

/** Fondu vers la pose « arme rangee » (1/s). */
const STOW_BLEND_RATE = 7;
/** Amplitude relative de balancement du bras libre / du bras qui tient l'arme quand elle est rangee. */
const FREE_ARM_SWING = 0.55;
const HOLD_ARM_SWING = 0.08;
/** Garde au sol minimale de la bouche du canon (unites du repere racine) et plage d'inclinaison du canon rangé (rad). */
const STOW_MUZZLE_CLEARANCE = 0.06;
const STOW_PITCH_MIN = 0.2;
const STOW_PITCH_MAX = 0.95;
/** Direction des pattes avant rangees : bras libre (gauche du chat) pendant, bras de l'arme (droit) un peu en avant. */
const STOW_DIR_L = new Vector3(0.06, -1, 0.12).normalize();
const STOW_DIR_R = new Vector3(-0.06, -1, 0.3).normalize();

const SHOULDER = CAT_LAYOUT.shoulder;
const MOUNT = CAT_LAYOUT.mount;
/** Vecteur epaule -> main de chaque patte avant en position de tir (identique a la geometrie des bras de CatFactory). */
const ARM_VEC_L = new Vector3(CAT_LAYOUT.handX + MOUNT.x - SHOULDER.x, MOUNT.y + CAT_LAYOUT.handY - SHOULDER.y, MOUNT.z - SHOULDER.z);
const ARM_VEC_R = new Vector3(-CAT_LAYOUT.handX + MOUNT.x + SHOULDER.x, MOUNT.y + CAT_LAYOUT.handY - SHOULDER.y, MOUNT.z - SHOULDER.z);

/** Rotation (Euler XYZ) d'une patte avant de la pose de tir vers la pose rangee, calculee une fois. */
const armStowEuler = (rest: Vector3, target: Vector3): Euler => new Euler().setFromQuaternion(new Quaternion().setFromUnitVectors(rest.clone().normalize(), target), 'XYZ');
const STOW_EULER_L = armStowEuler(ARM_VEC_L, STOW_DIR_L);
const STOW_EULER_R = armStowEuler(ARM_VEC_R, STOW_DIR_R);

// tampons de calcul (aucune allocation par frame)
const tipL = new Vector3();
const tipR = new Vector3();
const aimMount = new Vector3();

/**
 * Inclinaison du canon (rad, vers le bas) en pose rangee : aussi basse que possible pour un canon court (pistolet), relevee pour un long fusil
 * afin que la bouche (a `handHeight` + sa position locale) reste au-dessus du sol.
 */
const stowPitch = (rig: CatRig, handHeight: number): number => {
  const muzzle = rig.muzzle;
  if (!muzzle) return STOW_PITCH_MAX;
  const scale = muzzle.parent?.scale.z ?? 1;
  const my = muzzle.position.y * scale;
  const mz = muzzle.position.z * scale;
  const len = Math.hypot(my, mz);
  if (len < 1e-4) return STOW_PITCH_MAX;
  const phi = Math.atan2(my, mz);
  const bound = phi - Math.asin(Math.max(-1, Math.min(1, (STOW_MUZZLE_CLEARANCE - handHeight) / len)));
  return Math.max(STOW_PITCH_MIN, Math.min(STOW_PITCH_MAX, bound));
};

const wrapPi = (a: number): number => {
  const t = (a + Math.PI) % TAU;
  return (t < 0 ? t + TAU : t) - Math.PI;
};

/** Etat additionnel prive (fondu de marche) : evite de modifier le type fige CatAnimState. */
const walkBlend = new WeakMap<CatAnimState, { value: number }>();

/**
 * Anime `rig` a partir de `input` et `state` (tout vient de CAT_ANIM) : on reinitialise chaque os a rig.rest puis on compose
 * idle (respiration, queue, oreilles), marche (pattes, rebond, inclinaison), tir (recul, flash) et visee (buste + tete).
 * Retourne vrai si le flash de bouche est visible.
 */
export const animateCat = (rig: CatRig, state: CatAnimState, input: CatAnimInput): boolean => {
  const A = CAT_ANIM;
  const b = rig.bones;
  const dt = Math.max(0, input.dt);
  const phase = state.phaseOffset * TAU;
  const tt = input.time * TAU;

  // ---- pose de repos
  for (const [bone, r] of rig.rest) {
    bone.rotation.set(r.rx, r.ry, r.rz);
    bone.position.set(r.px, r.py, r.pz);
    bone.scale.set(1, 1, 1);
  }

  // ---- arme rangee : fondu lisse (le buste regarde alors dans le sens de la marche)
  state.stow += ((input.stowed ? 1 : 0) - state.stow) * (1 - Math.exp(-STOW_BLEND_RATE * dt));
  if (state.stow < 0.001) state.stow = 0;
  else if (state.stow > 0.999) state.stow = 1;
  const stow = state.stow;
  const aimAngle = input.heading + wrapPi(input.aimAngle - input.heading) * (1 - stow);

  // ---- cap du corps et visee du buste
  const turnRate = A.shoot.aimTurnSpeedDegPerSec * DEG;
  const follow = 1 - Math.exp(-turnRate * dt);
  const aimDiff = wrapPi(aimAngle - input.heading);
  const bodyTarget = Math.abs(aimDiff) > UPPER_YAW_LIMIT ? aimAngle - Math.sign(aimDiff) * UPPER_YAW_LIMIT : input.heading;
  const yawGoal = coreAngleToYaw(bodyTarget);
  b.root.rotation.y += wrapPi(yawGoal - b.root.rotation.y) * follow;
  const upperGoal = Math.max(-UPPER_YAW_LIMIT, Math.min(UPPER_YAW_LIMIT, wrapPi(coreAngleToYaw(aimAngle) - b.root.rotation.y)));
  state.upperYaw += (upperGoal - state.upperYaw) * follow;
  b.upper.rotation.y += state.upperYaw;
  b.head.rotation.y += state.upperYaw * HEAD_YAW_SHARE;

  // ---- marche : fondu + phase
  let blend = walkBlend.get(state);
  if (!blend) {
    blend = { value: 0 };
    walkBlend.set(state, blend);
  }
  const stepSpeed = Math.max(MIN_STEP_SPEED, input.speed01);
  blend.value += ((input.moving ? 1 : 0) - blend.value) * (1 - Math.exp(-WALK_BLEND_RATE * dt));
  const walk = blend.value;
  if (walk > 0.01) state.walkPhase += dt * A.walk.stepHz * TAU * stepSpeed;
  const stride = Math.sin(state.walkPhase);

  // ---- idle (toujours) : rebond, respiration
  const breathe = Math.sin(tt * A.idle.bobHz + phase);
  b.hips.position.y += breathe * A.idle.bobAmp;
  b.torso.scale.y = 1 + breathe * A.idle.breatheScale;

  // ---- queue : onde propagee, amplitude idle -> marche
  const tailAmp = (A.idle.tailSwayDeg + (A.walk.tailSwayDeg - A.idle.tailSwayDeg) * walk) * DEG;
  for (let i = 0; i < b.tail.length; i++) {
    const seg = b.tail[i];
    if (seg) seg.rotation.y += Math.sin(tt * A.idle.tailHz + phase + i * TAIL_PHASE_STEP) * tailAmp * (TAIL_AMP_BASE + i * TAIL_AMP_STEP);
  }

  // ---- oreilles : tressautement aleatoire
  state.earTimer -= dt;
  if (state.earTimer <= 0) {
    state.earTwitch = 1;
    state.earSide = Math.random() < 0.5 ? -1 : 1;
    const [lo, hi] = A.idle.earTwitchEvery;
    state.earTimer = lo + Math.random() * (hi - lo);
  }
  const twitch = state.earTwitch * A.idle.earTwitchDeg * DEG;
  if (state.earSide === 1) b.earL.rotation.z += twitch;
  else b.earR.rotation.z -= twitch;
  state.earTwitch *= Math.pow(EAR_DECAY_PER_FRAME, dt / FRAME_60);
  if (state.earTwitch < 0.001) state.earTwitch = 0;

  // ---- marche : pattes, bras, rebond, inclinaison, oreilles
  if (walk > 0.001) {
    const swing = A.walk.legSwingDeg * DEG * walk;
    b.legBL.rotation.x += stride * swing;
    b.legBR.rotation.x -= stride * swing;
    // arme rangee : la patte libre (gauche) balance franchement, celle qui tient l'arme (droite) presque pas
    b.armL.rotation.x -= stride * swing * (ARM_SWING_SHARE + (FREE_ARM_SWING - ARM_SWING_SHARE) * stow);
    b.armR.rotation.x += stride * swing * (ARM_SWING_SHARE + (HOLD_ARM_SWING - ARM_SWING_SHARE) * stow);
    b.hips.position.y += Math.abs(stride) * A.walk.bobAmp * walk;
    b.root.rotation.x = A.walk.leanDeg * DEG * walk;
    const bounce = A.walk.earBounceDeg * DEG * Math.abs(stride) * walk;
    b.earL.rotation.x += bounce;
    b.earR.rotation.x += bounce;
  } else {
    b.root.rotation.x = 0;
  }

  // ---- tir : recul de l'arme + du buste, flash de bouche
  if (input.shotSeq !== state.lastShotSeq) {
    state.kick = 1;
    state.flash = A.shoot.flashSeconds;
    state.lastShotSeq = input.shotSeq;
  }
  const kickback = input.kickback > 0 ? input.kickback : A.shoot.kickbackUnits;
  const mountRest = rig.rest.get(b.weaponMount);
  const mount = b.weaponMount;
  mount.position.z = (mountRest ? mountRest.pz : mount.position.z) - state.kick * kickback;
  b.upper.rotation.x -= state.kick * A.shoot.bodyRecoilDeg * DEG;
  b.armL.rotation.x += state.kick * kickback * ARM_RECOIL_PER_UNIT * 0.25;
  b.armR.rotation.x += state.kick * kickback * ARM_RECOIL_PER_UNIT * 0.25;
  aimMount.copy(mount.position);

  // ---- arme rangee : pattes avant le long du corps, arme tenue d'une main (droite) sur la hanche, canon vers le bas / l'avant
  if (stow > 0) {
    b.armL.rotation.x += STOW_EULER_L.x * stow;
    b.armL.rotation.y += STOW_EULER_L.y * stow;
    b.armL.rotation.z += STOW_EULER_L.z * stow;
    b.armR.rotation.x += STOW_EULER_R.x * stow;
    b.armR.rotation.y += STOW_EULER_R.y * stow;
    b.armR.rotation.z += STOW_EULER_R.z * stow;
    tipL.copy(ARM_VEC_L).applyQuaternion(b.armL.quaternion).add(b.armL.position);
    tipR.copy(ARM_VEC_R).applyQuaternion(b.armR.quaternion).add(b.armR.position);
    mount.position.lerp(tipR, stow);
    mount.rotation.x = stowPitch(rig, b.hips.position.y + b.upper.position.y + tipR.y) * stow;
  }
  // mains : autour de l'arme en position de tir (suivent le recul), aux extremites des pattes quand l'arme est rangee
  b.handL.position.set(aimMount.x + CAT_LAYOUT.handX, aimMount.y + CAT_LAYOUT.handY, aimMount.z);
  b.handR.position.set(aimMount.x - CAT_LAYOUT.handX, aimMount.y + CAT_LAYOUT.handY, aimMount.z);
  if (stow > 0) {
    b.handL.position.lerp(tipL, stow);
    b.handR.position.lerp(tipR, stow);
  }
  state.kick = Math.max(0, state.kick - dt / A.shoot.kickbackRecoverSeconds);
  state.flash = Math.max(0, state.flash - dt);
  return state.flash > 0;
};
